import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { AsyncSection } from '@/app/components/AsyncSection';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import { CompareChart } from '@/features/statistics/CompareChart';
import { RangePicker } from '@/features/statistics/RangePicker';
import type { DashboardRange } from '@/features/statistics/api';
import { useAsync } from '@/lib/useAsync';

/**
 * `/departments/compare` — the "View details" destination of the
 * Departments-overview compare card (§ Content-gebied › Drie lagen): the
 * full chart for every department the caller may see the statistics of, in
 * the active company (§ Bedrijfscontext).
 */
export function DepartmentComparePage() {
  const { t } = useTranslation();
  const { me, permissions } = useMe();
  const { company } = useActiveCompany();
  const [searchParams] = useSearchParams();
  const range: DashboardRange = (searchParams.get('range') as DashboardRange) || 'last_30_days';

  const companyId = company !== null ? company.id : null;
  const departments = useAsync(
    () =>
      companyId !== null
        ? apiGet('/companies/{companyId}/departments', { params: { companyId } })
        : Promise.resolve(me.departments ?? []),
    [companyId, me.departments],
  );

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('departments.compare.pageTitle')} lead={t('departments.compare.lead')} />
      <RangePicker />
      <AsyncSection state={departments} label={t('departments.compare.pageTitle')} skeleton="card">
        {(all) => {
          const targets = visibleDepartments(
            permissions,
            companyId,
            all.map((d) => ({ ...d, id: d.id ?? '', company_id: d.company_id ?? null })),
          ).filter((d) =>
            permissions.canViewDepartmentStats({
              id: d.id ?? '',
              company_id: d.company_id ?? null,
            }),
          );
          return targets.length === 0 ? (
            <EmptyState text={t('departments.compare.empty')} />
          ) : (
            <CompareChart
              title={t('departments.compare.pageTitle')}
              metricKey="steps"
              targets={targets.map((d) => ({ department_id: d.id ?? '' }))}
              range={range}
            />
          );
        }}
      </AsyncSection>
    </section>
  );
}
